import {
  getRoofFaces,
  getRoofingMaterial,
  getRoofingMaterialThickness,
  getRoofRidges,
  getRoofValleys,
} from "building-common/roofs/roofQuery";
import type { Building } from "building-types/building/buildingTypes";
import type {
  BuildingAnalyticBRep,
  BuildingAnalyticEntity,
  BuildingAnalyticEntityStyle,
  BuildingAnalyticFace,
} from "building-types/buildingAnalyticEntityTypes";
import {
  BuildingRoofAnalyticConditions,
  BuildingSlopeConditions,
  TAG_CONDITION_GABLE,
  TAG_POSITION_BOTTOM,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  buildingGeomAnalyticTagNew,
  FLASHING,
  HORIZONTAL,
  SLOPED,
  SOFFIT_DEPTH,
  SOFFIT_RAIL,
  SOFFIT,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { DoorUnit } from "building-types/doors/doorTypes";
import { isBuildingEntityHandle, type BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import { isBuildingAnalyticEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import { DEFAULT_ROOFING_MATERIAL_THICKNESS } from "building-types/roofs/roofConstants";
import type { WindowUnit } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { docEntityHandleIsGeneratedEntity } from "doc/entity/docEntityHandleTools";
import { materialSrcFromPalette } from "doc/materialLookup";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { aabb2Center } from "geom/aabb2";
import { aabb3Grow } from "geom/aabb3";
import type { Face } from "geom/brep";
import type { Line3Segment } from "geom/line3";
import { line3MidPt, line3ProjectPt, line3Slope } from "geom/line3";
import { obb2FromPathAndPolyLines, obb2GetCorners } from "geom/obb2";
import { planeContainsLine3, planeProjectVec3, polyLine2OnPlane } from "geom/plane";
import { polyLine2AABB2 } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { polyLine3AABB3, polyLine3Edge, polyLine3EdgeCount, polyLine3Vertex } from "geom/polyLine3";
import { unionCoplanarFaces } from "geom/unionFaces";
import { buildSoffitInnerBottomRail } from "../elements/roofs/roofSoffits";
import { addAnalyticLineSegment, addFaces, addPolyLine3s } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import memo from "../utils/memo";
import { getRoofEaves, getRoofRakes, getRoofRidgeVents } from "../utils/roofKeymeasures";

export const analyticConditionColors = {
  [BuildingRoofAnalyticConditions.ROOFING]: 0xffbbbb,
  [BuildingRoofAnalyticConditions.RIDGE_HORIZONTAL]: 0xff10f0,
  [BuildingRoofAnalyticConditions.RIDGE_SLOPED]: 0x00ffff,
  [BuildingRoofAnalyticConditions.VALLEY]: 0x00ffff,
  [BuildingRoofAnalyticConditions.FLASHING]: 0x0000ff,
  [BuildingRoofAnalyticConditions.EAVE]: 0xff10f0,
  [BuildingRoofAnalyticConditions.RAKE]: 0x00ffff,
  [BuildingRoofAnalyticConditions.SOFFIT]: 0xddaa22,
  [BuildingRoofAnalyticConditions.SOFFIT_LINE]: 0xffbb00,
  [BuildingSlopeConditions.SLOPED]: 0xff0000,
  [BuildingSlopeConditions.HORIZONTAL]: 0x0000ff,
};

const ROOF_EDGE_OPACITY = percentNew(0.7);
const ROOF_SURFACE_OPACITY = percentNew(0.1);

const ROOF = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.ROOF);
const ROOFING = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.ROOFING);
const RIDGE_VENT = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.RIDGE_VENT);
const RIDGE_HORIZONTAL = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.RIDGE_HORIZONTAL);
const RIDGE_SLOPED = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.RIDGE_SLOPED);
const VALLEY = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.VALLEY);
const EAVE = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.EAVE);
const RAKE = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.RAKE);

const FLASHING_SEARCH_TOLERANCE = 0.1;

const DEFAULT_ROOFING_MATERIAL = "roof0";

function roofEdgeStyle(condition: BuildingRoofAnalyticConditions): BuildingAnalyticEntityStyle {
  return {
    color: analyticConditionColors[condition],
    opacity: ROOF_EDGE_OPACITY,
    lineWidth3d: worldInchesNew(4),
  };
}

const ROOF_SURFACE_STYLE = {
  color: analyticConditionColors[BuildingRoofAnalyticConditions.ROOFING],
  opacity: ROOF_SURFACE_OPACITY,
  lineWidth3d: worldInchesNew(0.1),
};

const ROOF_SOFFIT_LINE_STYLE = {
  color: analyticConditionColors[BuildingRoofAnalyticConditions.SOFFIT_LINE],
  opacity: ROOF_SURFACE_OPACITY,
  lineWidth3d: worldInchesNew(3),
};

const HORIZONTAL_EDGE_STYLE = {
  color: analyticConditionColors[BuildingRoofAnalyticConditions.FLASHING],
  opacity: ROOF_EDGE_OPACITY,
  lineWidth3d: worldInchesNew(4),
};

const SLOPED_EDGE_STYLE = {
  color: analyticConditionColors[BuildingSlopeConditions.SLOPED],
  opacity: ROOF_EDGE_OPACITY,
  lineWidth3d: worldInchesNew(4),
};

export function addRoofSystemAnalyticEntities(
  building: ReturnType<typeof select>,
  buildingEntityHandle: BuildingEntityHandle,
  adjacentAnalyticEntities: Table<BuildingAnalyticEntity>,
): Table<BuildingAnalyticEntity> {
  const entities: RWTable<BuildingAnalyticEntity> = {};

  const roofSystem = building.roofSystems[buildingEntityHandle.id] as GeneratedRoofSystem | undefined;
  if (!roofSystem) {
    return entities;
  }

  const handle: DocEntityHandle = {
    type: "GENERATED_ENTITY",
    entity: buildingEntityHandle,
  };

  const { exteriorPalette } = building;

  addPolyLine3s(
    "ANALYTIC_ROOF",
    entities,
    BuildingRoofAnalyticConditions.RIDGE_VENT,
    [handle],
    getRoofRidgeVents(roofSystem),
    [ROOF, RIDGE_VENT],
    roofEdgeStyle(BuildingRoofAnalyticConditions.RIDGE_VENT),
  );

  addPolyLine3s(
    "ANALYTIC_ROOF",
    entities,
    BuildingRoofAnalyticConditions.RIDGE_HORIZONTAL,
    [handle],
    getRoofRidges(roofSystem, true),
    [ROOF, RIDGE_HORIZONTAL],
    roofEdgeStyle(BuildingRoofAnalyticConditions.RIDGE_HORIZONTAL),
  );
  addPolyLine3s(
    "ANALYTIC_ROOF",
    entities,
    BuildingRoofAnalyticConditions.RIDGE_SLOPED,
    [handle],
    getRoofRidges(roofSystem, false),
    [ROOF, RIDGE_SLOPED],
    roofEdgeStyle(BuildingRoofAnalyticConditions.RIDGE_SLOPED),
  );
  addPolyLine3s(
    "ANALYTIC_ROOF",
    entities,
    BuildingRoofAnalyticConditions.VALLEY,
    [handle],
    getRoofValleys(roofSystem),
    [ROOF, VALLEY],
    roofEdgeStyle(BuildingRoofAnalyticConditions.VALLEY),
  );
  addPolyLine3s(
    "ANALYTIC_ROOF",
    entities,
    BuildingRoofAnalyticConditions.EAVE,
    [handle],
    getRoofEaves(roofSystem),
    [ROOF, EAVE],
    roofEdgeStyle(BuildingRoofAnalyticConditions.EAVE),
  );
  addPolyLine3s(
    "ANALYTIC_ROOF",
    entities,
    BuildingRoofAnalyticConditions.RAKE,
    [handle],
    getRoofRakes(roofSystem),
    [ROOF, EAVE, RAKE],
    roofEdgeStyle(BuildingRoofAnalyticConditions.RAKE),
  );

  const facesByMaterialThickness: { [key: string]: Face[] } = {};
  for (const roof of Object.values(roofSystem.roofs)) {
    const { roofSettings, soffitRails, soffits } = roof;

    const thickness = getRoofingMaterialThickness(roofSettings);
    const material = getRoofingMaterial(roofSettings);

    const hash = `${material}|${thickness}`;

    if (!facesByMaterialThickness[hash]) {
      facesByMaterialThickness[hash] = [];
    }

    if (roof.trimsRoofMat) {
      for (const trimRoofMat of roof.trimsRoofMat) {
        const trimMaterial = trimRoofMat.material ?? material;
        const trimHash = `${trimMaterial}|${thickness}`;

        if (!facesByMaterialThickness[trimHash]) {
          facesByMaterialThickness[trimHash] = [];
        }

        facesByMaterialThickness[trimHash].push(...trimRoofMat.faces);
      }
    }

    for (const rails of Object.values(soffitRails)) {
      for (const rail of rails) {
        const { soffitHandle, innerPlane, outerPlane, outerBottomRail, innerBottomRail } = rail;

        const outerBottomRail3 = polyLine2OnPlane(outerPlane, outerBottomRail);
        const innerBottomRail3 = buildSoffitInnerBottomRail(innerPlane, outerPlane, innerBottomRail, outerBottomRail3);

        addPolyLine3s(
          "ANALYTIC_ROOF",
          entities,
          BuildingRoofAnalyticConditions.SOFFIT_LINE,
          [handle, soffitHandle],
          [outerBottomRail3],
          [SOFFIT, SOFFIT_RAIL],
          ROOF_SOFFIT_LINE_STYLE,
        );

        const [outerX0, outerY0, outerZ0] = polyLine3Vertex(outerBottomRail3, 0);
        const [outerX1, outerY1, outerZ1] = polyLine3Vertex(outerBottomRail3, 1);

        const [innerX0, innerY0, innerZ0] = polyLine3Vertex(innerBottomRail3, 0);
        const [innerX1, innerY1, innerZ1] = polyLine3Vertex(innerBottomRail3, 1);

        const [outerMidX, outerMidY, outerMidZ] = line3MidPt(outerX0, outerY0, outerZ0, outerX1, outerY1, outerZ1);

        const [innerMidX, innerMidY, innerMidZ] = line3ProjectPt(
          innerX0,
          innerY0,
          innerZ0,
          innerX1,
          innerY1,
          innerZ1,
          outerMidX,
          outerMidY,
          outerMidZ,
        );

        addAnalyticLineSegment(
          "ANALYTIC_ROOF",
          entities,
          "Soffit Depth",
          [handle, soffitHandle],
          [innerMidX, innerMidY, innerMidZ, outerMidX, outerMidY, outerMidZ],
          [SOFFIT, SOFFIT_DEPTH],
          ROOF_SOFFIT_LINE_STYLE,
        );
      }
    }

    for (const soffit of soffits) {
      if (!soffit.direction) {
        continue;
      }

      const [dirX, dirY, dirZ] = soffit.direction;

      const soffitHandle: DocEntityHandle = {
        type: "GENERATED_ENTITY",
        entity: { entityType: "ROOF_SOFFIT", id: soffit.id },
      };

      for (const face of soffit.faces) {
        if (!face.tags?.includes(TAG_POSITION_BOTTOM)) {
          continue;
        }

        // we only want to add rails from gable ends
        if (!face.tags?.includes(TAG_CONDITION_GABLE)) {
          continue;
        }

        const outerLoop = face.loops[0];
        if (!outerLoop || outerLoop.length < 4) {
          continue;
        }

        const { plane } = face;
        const [pdx, pdy] = planeProjectVec3(plane, dirX, dirY, dirZ);

        const [cx, cy] = aabb2Center(polyLine2AABB2(outerLoop));
        const path = polyLine2New([cx, cy, cx + pdx, cy + pdy]);
        const obb = obb2FromPathAndPolyLines(path, [outerLoop], true);
        if (!obb) {
          continue;
        }

        const corners = obb2GetCorners(obb);
        const railA2 = polyLine2New([corners[2], corners[3], corners[4], corners[5]]);
        const railB2 = polyLine2New([corners[0], corners[1], corners[6], corners[7]]);

        const railA = polyLine2OnPlane(plane, railA2);
        const railB = polyLine2OnPlane(plane, railB2);

        addPolyLine3s(
          "ANALYTIC_ROOF",
          entities,
          BuildingRoofAnalyticConditions.SOFFIT_LINE,
          [handle, soffitHandle],
          [railB],
          [SOFFIT, SOFFIT_RAIL],
          ROOF_SOFFIT_LINE_STYLE,
        );

        const [aX0, aY0, aZ0] = polyLine3Vertex(railA, 0);
        const [aX1, aY1, aZ1] = polyLine3Vertex(railA, 1);
        const [bX0, bY0, bZ0] = polyLine3Vertex(railB, 0);
        const [bX1, bY1, bZ1] = polyLine3Vertex(railB, 1);

        const [aMidX, aMidY, aMidZ] = line3MidPt(aX0, aY0, aZ0, aX1, aY1, aZ1);

        const [bProjX, bProjY, bProjZ] = line3ProjectPt(bX0, bY0, bZ0, bX1, bY1, bZ1, aMidX, aMidY, aMidZ);

        addAnalyticLineSegment(
          "ANALYTIC_ROOF",
          entities,
          "Soffit Depth",
          [handle, soffitHandle],
          [bProjX, bProjY, bProjZ, aMidX, aMidY, aMidZ],
          [SOFFIT, SOFFIT_DEPTH],
          ROOF_SOFFIT_LINE_STYLE,
        );
      }
    }

    facesByMaterialThickness[hash].push(...getRoofFaces(roof));
  }

  const addAwningRoofFaces = (unit: WindowUnit | DoorUnit) => {
    for (let i = 0; i < unit.awningRoof.geom.length; i++) {
      const geom = unit.awningRoof.geom[i];
      const tag = unit.awningRoof.tags[i];
      if (geom.type !== "FACE") {
        continue;
      }
      const material = tag.exteriorMaterial ?? DEFAULT_ROOFING_MATERIAL;
      const thickness = DEFAULT_ROOFING_MATERIAL_THICKNESS;
      const hash = `${material}|${thickness}`;
      if (!facesByMaterialThickness[hash]) {
        facesByMaterialThickness[hash] = [];
      }
      facesByMaterialThickness[hash].push(geom);
    }
  };

  const addFlashing = (roofFace: BuildingAnalyticFace) => {
    for (const loop of roofFace.face.loops) {
      const loop3 = polyLine2OnPlane(roofFace.face.plane, loop);
      const numEdges = polyLine3EdgeCount(loop3);
      for (let i = 0; i < numEdges; i++) {
        const edge: Line3Segment = polyLine3Edge(loop3, i) as unknown as Line3Segment;

        // find adjacent faces using spatial search
        const aabb3 = polyLine3AABB3(edge);
        aabb3Grow(aabb3, FLASHING_SEARCH_TOLERANCE, FLASHING_SEARCH_TOLERANCE, FLASHING_SEARCH_TOLERANCE);
        const adjacentFaces = BuildingGenerationContext.indexSpatialSearch(aabb3)
          .filter(docEntityHandleIsGeneratedEntity)
          .map((h) => h.entity)
          .filter(isBuildingEntityHandle)
          .filter((handle) => isBuildingAnalyticEntityType(handle.entityType))
          .map((h) => adjacentAnalyticEntities[h.id])
          .filter((entity): entity is BuildingAnalyticBRep => entity?.geometryType === "BREP")
          .filter(
            (e) => !!e?.brep.faces[0] && planeContainsLine3(e.brep.faces[0].plane, edge, FLASHING_SEARCH_TOLERANCE),
          );

        // flashing only exists if there are adjacent faces
        if (adjacentFaces.length === 0) {
          continue;
        }

        // add flashing line segment
        const isSloped = Math.abs(line3Slope(...edge)) > 0.01;
        const flashing = addAnalyticLineSegment(
          "ANALYTIC_ROOF",
          entities,
          BuildingRoofAnalyticConditions.FLASHING,
          [handle],
          edge,
          [ROOF, FLASHING, isSloped ? SLOPED : HORIZONTAL],
          isSloped ? SLOPED_EDGE_STYLE : HORIZONTAL_EDGE_STYLE,
        );

        BuildingGenerationContext.indexRelate(roofFace.docEntityHandle, "HOSTS", flashing.docEntityHandle);

        // relate flashing line segment to adjacent faces
        for (const face of adjacentFaces) {
          BuildingGenerationContext.indexRelate(face.docEntityHandle, "ADJACENT_TO", flashing.docEntityHandle);
        }
      }
    }
  };

  for (const doorUnit of Object.values(building.doorUnits)) {
    addAwningRoofFaces(doorUnit);
  }

  for (const windowUnit of Object.values(building.windowUnits)) {
    addAwningRoofFaces(windowUnit);
  }

  for (const materialThickness in facesByMaterialThickness) {
    const roofFaces = facesByMaterialThickness[materialThickness];
    let [material] = materialThickness.split("|");

    if (!material || material === "") {
      material = DEFAULT_ROOFING_MATERIAL;
    }

    const faces = addFaces(
      "ANALYTIC_ROOF",
      entities,
      ROOFING.condition,
      [handle],
      unionCoplanarFaces(roofFaces), // the geometry of roofs is sliced up, requiring unioning of coplanar faces
      { materialSrc: materialSrcFromPalette(exteriorPalette, material as ExteriorMaterial) },
      [ROOF, ROOFING],
      ROOF_SURFACE_STYLE,
    );

    for (const face of faces) {
      addFlashing(face);
    }
  }

  return entities;
}

const select = makeBuildingSelector([
  "roofSystems",
  "exteriorPalette",
  "doorUnits",
  "windowUnits",
  "analyticWallEntities",
  "analyticInteriorTrimEntities",
  "analyticRailingEntities",
  "analyticFloorStructureEntities",
]);

function generate(building: ReturnType<typeof select>): Pick<Building, "analyticRoofEntities"> {
  const analyticRoofEntities: RWTable<BuildingAnalyticEntity> = {};
  const adjacentAnalyticEntities: Table<BuildingAnalyticEntity> = {
    ...building.analyticWallEntities,
    ...building.analyticInteriorTrimEntities,
    ...building.analyticRailingEntities,
    ...building.analyticFloorStructureEntities,
  };
  for (const id of Object.keys(building.roofSystems)) {
    Object.assign(
      analyticRoofEntities,
      addRoofSystemAnalyticEntities(building, { entityType: "ROOF_SYSTEM", id }, adjacentAnalyticEntities),
    );
  }
  return { analyticRoofEntities };
}

export default memo(select, generate);
